import { safeJson } from "src/app/helpers/Index";
import { faTag, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { PromoCodeService } from "src/app/services/Index";
import "./AdminExtras.css";

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	saving: boolean;
	codes: any[];
	showDialog: boolean;
	edit: any;
};

const money = (pence: any) => `£${((Number(pence) || 0) / 100).toFixed(2)}`;
const shortDate = (d: any) => (d ? new Date(d).toLocaleDateString("en-GB") : null);

/** Codes get typed by hand and read aloud, so avoid the ambiguous characters. */
const suggestCode = () => {
	const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
	let code = "";
	for (let i = 0; i < 7; i++) code += alphabet[Math.floor(Math.random() * alphabet.length)];
	return code;
};

/**
 * Promo codes.
 *
 * The two caps are shown separately because they answer different questions —
 * how much the promotion costs, versus whether one person is taking the lot.
 */
export class AdminPromoCodes extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = { available: true, isLoading: true, saving: false, codes: [], showDialog: false, edit: null };
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const status = await safeJson(PromoCodeService.status());
			if (status?.available === false) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const codes = await safeJson(PromoCodeService.getAll());
			this.setState({ codes: Array.isArray(codes) ? codes : [], isLoading: false });
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}

	open(code?: any) {
		this.setState({
			edit: code
				? {
					...code,
					validFrom: code.validFrom ? new Date(code.validFrom) : null,
					validTo: code.validTo ? new Date(code.validTo) : null
				}
				: { code: suggestCode(), isPublished: false, name: "", percentageDiscount: 10 },
			showDialog: true
		});
	}

	async save() {
		const c = this.state.edit;
		if (!c?.name?.trim() || !c?.code?.trim()) return;

		this.setState({ saving: true });
		const payload = {
			code: c.code.trim().toUpperCase(),
			isPublished: !!c.isPublished,
			name: c.name.trim(),
			percentageDiscount: Number(c.percentageDiscount) || 0,
			totalUses: c.totalUses || null,
			usesPerAccount: c.usesPerAccount || null,
			validFrom: c.validFrom || null,
			validTo: c.validTo || null
		};

		const res = c.id ? await PromoCodeService.update(c.id, payload) : await PromoCodeService.add(payload);
		this.setState({ saving: false });

		if (!res.ok) {
			const message = await res.text();
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async remove(code: any) {
		if (!window.confirm(`Remove "${code.name}"? Orders that used it keep working.`)) return;
		await PromoCodeService.remove(code.id);
		this.load();
	}

	render() {
		const { available, isLoading, codes } = this.state;
		const edit = this.state.edit;

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Promo codes</h2>
					<div className="ex-empty">
						<FontAwesomeIcon icon={faTag} />
						<h3>Promo codes aren&rsquo;t switched on yet</h3>
						<p>The promo code tables haven&rsquo;t been created on this environment.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 6 }}>Promo codes</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 20 }}>
					Percentage discounts a fan types at checkout.
				</p>

				<div style={{ marginBottom: 18 }}>
					<Button icon="pi pi-plus" label="Add code" onClick={() => this.open()} />
				</div>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && codes.length === 0 &&
					<div className="ex-empty">
						<FontAwesomeIcon icon={faTag} />
						<h3>No codes yet</h3>
						<p>Useful for an early-bird push, a partner offer, or saying sorry after a postponement.</p>
					</div>}

				{codes.length > 0 &&
					<div className="ex-table">
						<div className="ex-tr ex-th">
							<span>Promotion</span><span>Code</span><span>Off</span><span>Used</span><span>Given away</span><span>Live</span><span />
						</div>
						{codes.map((c) => (
							<div className="ex-tr" key={`c-${c.id}`}>
								<span>
									{c.name}
									{(c.validFrom || c.validTo) &&
										<small>{shortDate(c.validFrom) || "any time"} &ndash; {shortDate(c.validTo) || "no end"}</small>}
								</span>
								<span><code className="ex-code">{c.code}</code></span>
								<span>{Number(c.percentageDiscount)}%</span>
								<span>
									{c.redeemed}{c.totalUses ? ` / ${c.totalUses}` : ""}
									{c.usesPerAccount && <small>max {c.usesPerAccount} each</small>}
								</span>
								<span>{money(c.discountGivenPence)}</span>
								<span className={c.isLiveNow ? "ex-live" : "ex-dim"}>{c.isLiveNow ? "Live" : c.isPublished ? "Scheduled" : "Draft"}</span>
								<span className="ex-row-actions">
									<Button className="p-button-text" label="Edit" onClick={() => this.open(c)} style={{ padding: "2px 6px" }} />
									<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.remove(c)} style={{ padding: "2px 6px" }} />
								</span>
							</div>
						))}
					</div>}

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={edit?.id ? "Edit promo code" : "New promo code"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 480 }} visible={this.state.showDialog}>
					{edit && <>
						<label className="ex-label">What you call it</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })} placeholder="Early bird 10%" style={{ marginBottom: 14, width: "100%" }} value={edit.name || ""} />

						<label className="ex-label">What the fan types</label>
						<div className="ex-two">
							<InputText onChange={(e) => this.setState({ edit: { ...edit, code: e.target.value.toUpperCase() } })} style={{ flex: 1 }} value={edit.code || ""} />
							<Button className="p-button-outlined" label="New code" onClick={() => this.setState({ edit: { ...edit, code: suggestCode() } })} />
						</div>

						<label className="ex-label" style={{ marginTop: 14 }}>Discount</label>
						<InputNumber max={100} min={1} onValueChange={(e) => this.setState({ edit: { ...edit, percentageDiscount: e.value } })} suffix="%" style={{ marginBottom: 14, width: "100%" }} value={edit.percentageDiscount} />

						<div className="ex-two">
							<div>
								<label className="ex-label">Total uses</label>
								<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, totalUses: e.value } })} placeholder="Unlimited" style={{ width: "100%" }} value={edit.totalUses} />
							</div>
							<div>
								<label className="ex-label">Uses per person</label>
								<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, usesPerAccount: e.value } })} placeholder="Unlimited" style={{ width: "100%" }} value={edit.usesPerAccount} />
							</div>
						</div>
						<p className="ex-hint">
							Two different questions: total is what the promotion costs you, per-person stops one buyer taking the lot.
						</p>

						<div className="ex-two" style={{ marginTop: 14 }}>
							<div>
								<label className="ex-label">Starts</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, validFrom: e.value } })} showButtonBar style={{ width: "100%" }} value={edit.validFrom} />
							</div>
							<div>
								<label className="ex-label">Expires</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, validTo: e.value } })} showButtonBar style={{ width: "100%" }} value={edit.validTo} />
							</div>
						</div>

						<div className="ex-switch" style={{ marginTop: 18 }}>
							<InputSwitch checked={!!edit.isPublished} onChange={(e) => this.setState({ edit: { ...edit, isPublished: e.value } })} />
							<label>Published</label>
						</div>
						<p className="ex-hint">A draft code never works, whatever the dates say.</p>
					</>}
				</Dialog>
			</div>
		);
	}
}
